import { useCallback, useEffect, useMemo, useState } from "react";
import {
  RUN_REPORTS_PREVIEW_QUERY_LIMIT,
  RUN_REPORTS_REPORT_DEFINITION_QUERY_PARAM,
  AssistantReportDefinitionSchema,
  resolveRunReportsAcademicYear,
  resolveUserFacingRunReportsPreviewWarning,
  type AssistantReportDefinition,
  type AssistantReportExportFormat,
  type AssistantReportPreviewRow,
  type IntellioAssistantArtifactRef,
  type IntellioAssistantReportSchedule,
  type IntellioAssistantReportTemplate,
} from "@triedge/domain-core";
import { readDomainRouterErrorMessageWhenPresent } from "../state/domainRouterError";
import {
  useDomainRouterMutation,
  useDomainRouterQuery,
  useLazyDomainRouterQuery,
} from "../state/domainRouterHooks";
import { useAppDispatch, useAppSelector } from "../state/hooks";
import { useReportGenerationFilterModel } from "./useReportGenerationFilterModel";
import { REPORT_GENERATION_ENTITIES_BY_ID } from "./reportGenerationSemanticCatalog";
import {
  applyReportGenerationRouteContextToDefinition,
  resolveReportGenerationRouteContext,
} from "./reportGenerationRouteContext";
import {
  closeReportGenerationExport,
  closeReportGenerationWorkspace,
  closeReportGenerationTemplateVersionHistory,
  dismissReportGenerationInsight,
  openReportGenerationTemplateVersionHistory,
  openReportGenerationExport,
  openReportGenerationWorkspace,
  resetReportGenerationWorkspaceView,
  setReportGenerationContextMenuField,
  setReportGenerationSelectedHistoryEntry,
  setReportGenerationTemplateView,
  setReportGenerationWorkspaceCollapsed,
  setReportGenerationWorkspaceTab,
  setReportGenerationWorkspaceTitle,
  toggleReportGenerationHiddenColumn,
  toggleReportGenerationWorkspaceSort,
  type ReportGenerationWorkspaceTab,
} from "./reportGenerationWorkspaceSlice";
import type {
  ReportGenerationFieldOption,
  ReportGenerationFilterState,
} from "./reportGenerationFiltering";
import { sortReportGenerationPreviewRows } from "./reportGenerationPreviewSorting";
import { buildReportGenerationAdvancedVisualizationSnapshot } from "./reportGenerationAdvancedVisualization";

export type ReportGenerationWorkspaceTemplateItem = {
  id: string;
  name: string;
  description: string;
  moduleLabel: string;
  source: "gallery" | "custom";
  visibility?: "Private" | "Shared";
  lastModified?: string;
  version?: string;
  isScheduled?: boolean;
  entities: string[];
  fields: string[];
  conditions?: ReportGenerationFilterState["conditions"];
  sorts?: ReportGenerationFilterState["sorts"];
  reportDefinition?: AssistantReportDefinition;
};

export type ReportGenerationHistoryItem = {
  id: string;
  runId: string | null;
  workItemId: string | null;
  templateId: string | null;
  templateName: string;
  runDate: string;
  format: "XLSX" | "CSV" | "PDF";
  status: "Completed" | "Scheduled" | "Running" | "Failed" | "Cancelled";
  rowCount?: number | null;
  statusMessage?: string | null;
  artifactRefs: ReportGenerationArtifactRef[];
};

export type ReportGenerationArtifactRef = Pick<
  IntellioAssistantArtifactRef,
  | "id"
  | "artifactType"
  | "label"
  | "fileName"
  | "mimeType"
  | "downloadUrl"
  | "expiresAtEpochMs"
  | "sourceRef"
>;

export type ReportGenerationInsightCard = {
  id: string;
  tone: "up" | "down" | "alert";
  headline: string;
  detail: string;
};

export type ReportGenerationFieldAuthority = {
  scope: string;
  status: string;
  source?: string | null;
  domainPackage?: string | null;
  domainType?: string | null;
  domainSchema?: string | null;
  operationId: string;
  operationKind: string;
  appSyncRouter: string;
  appSyncField: string;
  resultPath: string;
  routeFamily?: string | null;
  datasourceFamily?: string | null;
  joinKey?: string | null;
  permission?: string | null;
};

export type ReportGenerationPreviewColumn = {
  fieldId: string;
  label: string;
  moduleLabel: string;
  entityId?: string | null;
  authority?: ReportGenerationFieldAuthority | null;
};

export type ReportGenerationPreviewRow = {
  id: string;
  cells: Record<string, AssistantReportPreviewRow["cells"][string]>;
};

export type ReportGenerationVersionHistoryEntry = {
  version: string;
  date: string;
  summary: string;
};

const formatIsoDate = (
  value: number | null | undefined,
): string | undefined => {
  if (!value || !Number.isFinite(value)) {
    return undefined;
  }
  return new Date(value).toISOString().slice(0, 10);
};

const formatUnixDate = (value: number | null | undefined): string => {
  if (!value || !Number.isFinite(value)) {
    return "";
  }
  return new Date(value * 1000).toISOString().slice(0, 10);
};

const toDisplayVisibility = (
  value: IntellioAssistantReportTemplate["visibility"],
): "Private" | "Shared" => (value === "shared" ? "Shared" : "Private");

const toFilterStateFromDefinition = (
  definition: AssistantReportDefinition,
): ReportGenerationFilterState => ({
  selectedEntities: [...definition.selectedEntities],
  selectedFields: [...definition.selectedFields],
  conditions: definition.conditions.map((condition) => ({ ...condition })),
  dateRange: {
    preset: definition.dateRange?.preset ?? "Custom",
    from: definition.dateRange?.from ?? "",
    to: definition.dateRange?.to ?? "",
  },
  sorts: definition.sorts.map((sort) => ({ ...sort })),
});

const stableDefinitionKey = (definition: AssistantReportDefinition): string =>
  JSON.stringify({
    title: definition.title,
    pathname: definition.pathname ?? null,
    module: definition.module ?? null,
    routeFamily: definition.routeFamily ?? null,
    selectedEntities: definition.selectedEntities,
    selectedFields: definition.selectedFields,
    conditions: definition.conditions,
    dateRange: definition.dateRange,
    sorts: definition.sorts,
  });

const resolveTemplateModuleLabel = (
  definition: AssistantReportDefinition,
): string => {
  const firstEntity = definition.selectedEntities
    .map((entityId) => REPORT_GENERATION_ENTITIES_BY_ID[entityId])
    .find(Boolean);
  if (firstEntity) {
    return firstEntity.moduleLabel;
  }
  switch (definition.routeFamily) {
    case "financial-aid":
      return "Financial Aid";
    case "students-families":
      return "Students & Families";
    case "tuition":
      return "Tuition";
    case "settings":
      return "Cross-Module";
    case "admissions":
    default:
      return "Admissions";
  }
};

const pickHistoryFormat = (
  artifactRefs:
    | Array<{ fileName?: string | null; mimeType?: string | null }>
    | undefined,
): "XLSX" | "CSV" | "PDF" => {
  const refs = artifactRefs ?? [];
  if (
    refs.some(
      (artifact) =>
        String(artifact.fileName ?? "")
          .toLowerCase()
          .endsWith(".pdf") || String(artifact.mimeType ?? "").includes("pdf"),
    )
  ) {
    return "PDF";
  }
  if (
    refs.some(
      (artifact) =>
        String(artifact.fileName ?? "")
          .toLowerCase()
          .endsWith(".csv") || String(artifact.mimeType ?? "").includes("csv"),
    )
  ) {
    return "CSV";
  }
  return "XLSX";
};

const toReportGenerationArtifactRef = (
  artifact: IntellioAssistantArtifactRef,
): ReportGenerationArtifactRef => ({
  id: artifact.id,
  artifactType: artifact.artifactType,
  label: artifact.label,
  fileName: artifact.fileName,
  mimeType: artifact.mimeType,
  downloadUrl: artifact.downloadUrl,
  expiresAtEpochMs: artifact.expiresAtEpochMs,
  sourceRef: artifact.sourceRef,
});

const readHistoryRowCount = (item: {
  resultSummary?: string | null;
  statusMessage?: string | null;
}): number | null => {
  const summary = [item.resultSummary, item.statusMessage]
    .map((value) => String(value ?? "").trim())
    .find(Boolean);
  if (!summary) {
    return null;
  }
  const match = summary.match(/\b(\d{1,9})\s+rows?\b/i);
  if (!match?.[1]) {
    return null;
  }
  const parsed = Number(match[1]);
  return Number.isFinite(parsed) ? parsed : null;
};

const extractTemplateIdFromSourceRef = (
  sourceRef: string | null | undefined,
): string | null => {
  const normalized = String(sourceRef ?? "").trim();
  if (normalized.startsWith("assistant-report-template:")) {
    return normalized.slice("assistant-report-template:".length) || null;
  }
  return null;
};

const parseHistoryDefinition = (
  value: unknown,
): AssistantReportDefinition | null => {
  const parsed = AssistantReportDefinitionSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
};

const RUN_REPORTS_DEFINITION_QUERY_PARAM =
  RUN_REPORTS_REPORT_DEFINITION_QUERY_PARAM;

export const readRunReportsDefinitionHandoffFromLocation = (): AssistantReportDefinition | null => {
  if (typeof window === "undefined") {
    return null;
  }
  const raw = new URLSearchParams(window.location.search).get(
    RUN_REPORTS_DEFINITION_QUERY_PARAM,
  );
  if (!raw) {
    return null;
  }
  try {
    return parseHistoryDefinition(JSON.parse(raw));
  } catch {
    return null;
  }
};

export const hasRunReportsDefinitionHandoff = (): boolean =>
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).has(RUN_REPORTS_DEFINITION_QUERY_PARAM);

const clearRunReportsDefinitionHandoffFromUrl = (): void => {
  if (typeof window === "undefined") {
    return;
  }
  const url = new URL(window.location.href);
  if (!url.searchParams.has(RUN_REPORTS_DEFINITION_QUERY_PARAM)) {
    return;
  }
  url.searchParams.delete(RUN_REPORTS_DEFINITION_QUERY_PARAM);
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
};

const YSTNJ_REPORT_TIMEZONE = "America/New_York";

export const resolveReportGenerationPreviewTransportError = (
  error: unknown,
  isError: boolean,
): string | null => {
  const message = readDomainRouterErrorMessageWhenPresent(error, isError);
  if (!message) return null;
  return "We couldn't load this preview. Review your report selections and try again. If the problem continues, contact support.";
};

const AMBIGUOUS_REPORT_TITLE = /^(?:untitled|new) report$/i;

export const resolveTemplateRunTitle = (
  input: {
    name: string;
    description?: string | null;
    reportDefinition?: unknown;
  },
  now: Date = new Date(),
): string => {
  const includesAcademicYear = /\b20\d{2}[-–]20\d{2}\b/.test(
    String(input.description ?? ""),
  );
  if (!includesAcademicYear) return input.name;
  const reportDefinition = input.reportDefinition as
    | Pick<AssistantReportDefinition, "dateRange">
    | null
    | undefined;
  const academicYear = resolveRunReportsAcademicYear(
    reportDefinition ?? {
      dateRange: { preset: "This Year", from: null, to: null },
    },
    now,
    YSTNJ_REPORT_TIMEZONE,
  );
  return `${academicYear} ${input.name.replace(/^YSTNJ\s+/i, "")}`;
};

export const resolveUserFacingPreviewWarning =
  resolveUserFacingRunReportsPreviewWarning;

export const resolveUserFacingReportTitle = (
  title: string | null | undefined,
  runDate: string,
): string => {
  const normalized = String(title ?? "").trim();
  return normalized && !AMBIGUOUS_REPORT_TITLE.test(normalized)
    ? normalized
    : `Saved report – ${runDate}`;
};

export const resolveUserFacingHistoryStatusMessage = (input: {
  title: string;
  state: string;
  message: string | null | undefined;
  artifactCount: number;
}): string | null => {
  if (input.state === "completed") {
    return input.artifactCount > 0
      ? `${input.title} is ready to download.`
      : `${input.title} finished, but no downloadable files were recorded.`;
  }
  if (input.state === "failed") {
    return "We couldn't finish this report. Review the report selections and try again.";
  }
  if (input.state === "cancelled") return "This report was cancelled before it finished.";
  return "This report is processing. Downloads will appear here when it is ready.";
};

export const useReportGenerationWorkspaceModel = (input?: {
  defaultTitle?: string;
  enabled?: boolean;
  pathname?: string;
  module?: AssistantReportDefinition["module"];
}) => {
  const dispatch = useAppDispatch();
  const workspace = useAppSelector((state) => state.reportGenerationWorkspace);
  const [lastActionFeedback, setLastActionFeedback] = useState<string | null>(
    null,
  );
  const [pendingReportRun, setPendingReportRun] = useState<{
    runId: string | null;
    workItemId: string | null;
    requestedFormats: AssistantReportExportFormat[];
  } | null>(null);
  const enabled = input?.enabled ?? true;
  const routeContext = useMemo(
    () => resolveReportGenerationRouteContext(input),
    [input?.defaultTitle, input?.enabled, input?.module, input?.pathname],
  );
  const isRunReportsRoute = routeContext.pathname === "/school/run-reports";
  const filterModel = useReportGenerationFilterModel(
    isRunReportsRoute
      ? {
          authorityScope: "all",
          includeLegacy: true,
        }
      : undefined,
  );
  const { pathname, module, routeFamily } = routeContext;

  const currentDefinition = useMemo<AssistantReportDefinition>(
    () => ({
      title: workspace.reportTitle.trim() || routeContext.defaultTitle,
      pathname,
      module,
      routeFamily,
      templateId: filterModel.state.selectedTemplateId,
      templateSource: filterModel.state.selectedTemplateSource,
      selectedEntities: [...filterModel.viewModel.state.selectedEntities],
      selectedFields: [...filterModel.viewModel.state.selectedFields],
      conditions: filterModel.viewModel.state.conditions.map((condition) => ({
        ...condition,
      })),
      dateRange: {
        preset: filterModel.viewModel.state.dateRange.preset,
        from: filterModel.viewModel.state.dateRange.from || null,
        to: filterModel.viewModel.state.dateRange.to || null,
      },
      sorts: filterModel.viewModel.state.sorts.map((sort) => ({ ...sort })),
    }),
    [
      filterModel.state.selectedTemplateId,
      filterModel.state.selectedTemplateSource,
      filterModel.viewModel.state.conditions,
      filterModel.viewModel.state.dateRange.from,
      filterModel.viewModel.state.dateRange.preset,
      filterModel.viewModel.state.dateRange.to,
      filterModel.viewModel.state.selectedEntities,
      filterModel.viewModel.state.selectedFields,
      filterModel.viewModel.state.sorts,
      module,
      pathname,
      routeFamily,
      routeContext.defaultTitle,
      workspace.reportTitle,
    ],
  );

  const visibleReportDefinition = useMemo<AssistantReportDefinition>(() => {
    const hiddenFieldIds = new Set(workspace.hiddenColumns);
    return {
      ...currentDefinition,
      selectedFields: currentDefinition.selectedFields.filter(
        (fieldId) => !hiddenFieldIds.has(fieldId),
      ),
    };
  }, [currentDefinition, workspace.hiddenColumns]);

  const workspaceQuery = useDomainRouterQuery(
    "getAssistantReportGenerationWorkspace",
    {
      input: {
        pathname,
        module,
        reportDefinition: currentDefinition,
        previewLimit: RUN_REPORTS_PREVIEW_QUERY_LIMIT,
        templateLimit: 100,
        scheduleLimit: 100,
        workItemLimit: 100,
        includeSharedTemplates: true,
        includeArchivedTemplates: false,
        includeDisabledSchedules: true,
        includeClosedWorkItems: true,
      },
    },
    { skip: !enabled },
  );
  const previewQuery = {
    data: workspaceQuery.data?.preview,
    error: workspaceQuery.error,
    isError: workspaceQuery.isError,
    isLoading: workspaceQuery.isLoading,
    isFetching: workspaceQuery.isFetching,
  };
  const [triggerGetAssistantSessionState] =
    useLazyDomainRouterQuery<"getAssistantSessionState">();
  const [triggerMutateAssistantRun, mutateRunState] =
    useDomainRouterMutation("mutateAssistantRun");
  const [triggerMutateAssistantReportTemplate, mutateTemplateState] =
    useDomainRouterMutation("mutateAssistantReportTemplate");
  const [triggerMutateAssistantReportSchedule, mutateScheduleState] =
    useDomainRouterMutation("mutateAssistantReportSchedule");

  const galleryTemplates = useMemo<ReportGenerationWorkspaceTemplateItem[]>(
    () =>
      filterModel.viewModel.templates.available.map((template) => ({
        id: template.id,
        name: template.name,
        description: template.description,
        moduleLabel: template.moduleLabel,
        source: "gallery",
        entities: [...template.entities],
        fields: [...template.fields],
        conditions: template.conditions?.map((condition) => ({ ...condition })),
        sorts: template.sorts?.map((sort) => ({ ...sort })),
      })),
    [filterModel.viewModel.templates.available],
  );

  const savedTemplates = workspaceQuery.data?.templates.items ?? [];
  const schedules = workspaceQuery.data?.schedules.items ?? [];

  const myTemplates = useMemo<ReportGenerationWorkspaceTemplateItem[]>(() => {
    const enabledSchedulesByTemplateId = new Map<
      string,
      IntellioAssistantReportSchedule[]
    >();
    for (const schedule of schedules) {
      if (!schedule.reportTemplateId) {
        continue;
      }
      const list =
        enabledSchedulesByTemplateId.get(schedule.reportTemplateId) ?? [];
      list.push(schedule);
      enabledSchedulesByTemplateId.set(schedule.reportTemplateId, list);
    }
    return savedTemplates.map((template) => {
      const reportDefinition = applyReportGenerationRouteContextToDefinition(
        template.reportDefinition,
        routeContext,
      );
      return {
        id: template.id,
        name: template.title,
        description: template.description ?? "Saved report template",
        moduleLabel: resolveTemplateModuleLabel(reportDefinition),
        source: "custom",
        visibility: toDisplayVisibility(template.visibility),
        lastModified: formatIsoDate(template.updatedAtEpochMs),
        version: String(template.version),
        isScheduled: (enabledSchedulesByTemplateId.get(template.id) ?? []).some(
          (schedule) => schedule.enabled,
        ),
        entities: [...reportDefinition.selectedEntities],
        fields: [...reportDefinition.selectedFields],
        reportDefinition,
      };
    });
  }, [routeContext, savedTemplates, schedules]);

  const historyEntries = useMemo<ReportGenerationHistoryItem[]>(
    () =>
      (workspaceQuery.data?.history.items ?? [])
        .filter(
          (item) =>
            item.workflowKey === "assistant.report.generate" ||
            item.sourceRef === "assistant-report:inline" ||
            String(item.sourceRef ?? "").startsWith(
              "assistant-report-template:",
            ) ||
            String(item.sourceRef ?? "").startsWith(
              "assistant-report-schedule:",
            ),
        )
        .map((item) => {
          const artifactRefs = (item.artifactRefs ?? []).map(
            toReportGenerationArtifactRef,
          );
          const runDate = formatUnixDate(item.updatedAt ?? item.createdAt);
          const templateName = resolveUserFacingReportTitle(item.title, runDate);
          return {
            id: item.id,
            runId: item.runId ?? null,
            workItemId: item.id,
            templateId: extractTemplateIdFromSourceRef(item.sourceRef ?? null),
            templateName,
            runDate,
            format: pickHistoryFormat(item.artifactRefs),
            status:
              item.state === "completed"
                ? "Completed"
                : item.state === "failed"
                  ? "Failed"
                  : item.state === "cancelled"
                    ? "Cancelled"
                    : String(item.sourceRef ?? "").startsWith(
                          "assistant-report-schedule:",
                        )
                      ? "Scheduled"
                      : "Running",
            rowCount: readHistoryRowCount(item),
            statusMessage: resolveUserFacingHistoryStatusMessage({
              title: templateName,
              state: item.state,
              message: item.resultSummary ?? item.statusMessage ?? null,
              artifactCount: artifactRefs.length,
            }),
            artifactRefs,
          };
        }),
    [workspaceQuery.data?.history.items],
  );

  const currentDefinitionKey = useMemo(
    () => stableDefinitionKey(currentDefinition),
    [currentDefinition],
  );

  const currentCustomTemplateId =
    filterModel.state.selectedTemplateSource === "custom"
      ? filterModel.state.selectedTemplateId
      : null;
  const currentSchedule = useMemo(() => {
    if (currentCustomTemplateId) {
      return (
        schedules.find(
          (schedule) => schedule.reportTemplateId === currentCustomTemplateId,
        ) ?? null
      );
    }
    return (
      schedules.find(
        (schedule) =>
          stableDefinitionKey(
            applyReportGenerationRouteContextToDefinition(
              schedule.reportDefinition,
              routeContext,
            ),
          ) === currentDefinitionKey,
      ) ?? null
    );
  }, [currentCustomTemplateId, currentDefinitionKey, routeContext, schedules]);

  const previewColumns = useMemo<ReportGenerationPreviewColumn[]>(
    () =>
      (previewQuery.data?.columns ?? [])
        .filter((field) => !workspace.hiddenColumns.includes(field.fieldId))
        .map((field) => ({
          fieldId: field.fieldId,
          label: field.label,
          moduleLabel: field.moduleLabel,
          entityId: field.entityId ?? null,
          authority: field.authority ?? null,
        })),
    [previewQuery.data?.columns, workspace.hiddenColumns],
  );

  const runnableReportDefinition = useMemo<AssistantReportDefinition>(() => {
    const visibleFieldIds = previewColumns.map((column) => column.fieldId);
    return {
      ...currentDefinition,
      selectedFields:
        visibleFieldIds.length > 0
          ? visibleFieldIds
          : visibleReportDefinition.selectedFields,
    };
  }, [currentDefinition, previewColumns, visibleReportDefinition.selectedFields]);

  const previewRows = useMemo<ReportGenerationPreviewRow[]>(() => {
    const rows = (previewQuery.data?.rows ?? []).map((row) => ({
      id: row.id,
      cells: { ...row.cells },
    }));
    return sortReportGenerationPreviewRows(rows, workspace.sortColumn, workspace.sortDirection);
  }, [previewQuery.data?.rows, workspace.sortColumn, workspace.sortDirection]);

  const canPreview = previewColumns.length > 0 && previewRows.length > 0;
  const resolvedTitle = currentDefinition.title;
  const hasReportDefinitionSelection =
    currentDefinition.selectedEntities.length > 0 &&
    currentDefinition.selectedFields.length > 0;
  const joins = previewQuery.data?.joins ?? filterModel.viewModel.joins;
  const previewWarnings = (previewQuery.data?.warnings ?? [])
    .map(resolveUserFacingPreviewWarning)
    .filter(Boolean);
  const previewTransportError = resolveReportGenerationPreviewTransportError(
    previewQuery.error,
    previewQuery.isError,
  );
  const totalRowCount = previewQuery.data?.totalRowCount ?? previewRows.length;

  const advancedVisualization = useMemo(
    () =>
      buildReportGenerationAdvancedVisualizationSnapshot({
        actorSource: "cognito-profile",
        profile: filterModel.profile,
        effectivePersona: filterModel.effectivePersona,
        definition: previewQuery.data?.definition ?? currentDefinition,
        modules: filterModel.viewModel.modules,
        inaccessibleEntities: filterModel.viewModel.inaccessibleEntities,
        availableFields: filterModel.viewModel.availableFields,
        preview: previewQuery.data ?? null,
        previewColumns,
        previewRows,
        joins,
        totalRowCount,
        warnings: previewWarnings,
      }),
    [
      currentDefinition,
      filterModel.effectivePersona,
      filterModel.profile,
      filterModel.viewModel.availableFields,
      filterModel.viewModel.inaccessibleEntities,
      filterModel.viewModel.modules,
      joins,
      previewColumns,
      previewRows,
      previewQuery.data,
      previewWarnings,
      totalRowCount,
    ],
  );

  const restoreDefinition = useCallback(
    (definition: AssistantReportDefinition) => {
      const contextualDefinition =
        applyReportGenerationRouteContextToDefinition(definition, routeContext);
      filterModel.actions.replaceDefinition({
        filterState: toFilterStateFromDefinition(contextualDefinition),
        templateId:
          contextualDefinition.templateSource === "custom"
            ? (contextualDefinition.templateId ?? null)
            : (contextualDefinition.templateId ?? null),
        source: contextualDefinition.templateSource ?? null,
      });
      dispatch(resetReportGenerationWorkspaceView());
      dispatch(
        openReportGenerationWorkspace({
          activeTab: "filters",
          reportTitle: contextualDefinition.title,
        }),
      );
    },
    [dispatch, filterModel.actions, routeContext],
  );

  useEffect(() => {
    if (pathname !== "/school/run-reports") {
      return;
    }
    const handoffDefinition = readRunReportsDefinitionHandoffFromLocation();
    if (!handoffDefinition) {
      return;
    }
    restoreDefinition(handoffDefinition);
    clearRunReportsDefinitionHandoffFromUrl();
  }, [pathname, restoreDefinition]);

  const applyTemplateById = useCallback(
    (templateId: string, source: "gallery" | "custom") => {
      const template = (
        source === "gallery" ? galleryTemplates : myTemplates
      ).find((entry) => entry.id === templateId);
      if (!template) {
        return;
      }
      if (source === "custom" && template.reportDefinition) {
        restoreDefinition({
          ...template.reportDefinition,
          title: template.reportDefinition.title || template.name,
          templateId: template.id,
          templateSource: "custom",
        });
        return;
      }
      filterModel.actions.applyTemplate({
        templateId: template.id,
        source,
        entities: template.entities,
        fields: template.fields,
        conditions: template.conditions,
        sorts: template.sorts,
      });
      dispatch(
        openReportGenerationWorkspace({
          activeTab: "filters",
          reportTitle: resolveTemplateRunTitle(template),
        }),
      );
    },
    [
      dispatch,
      filterModel.actions,
      galleryTemplates,
      myTemplates,
      restoreDefinition,
    ],
  );

  const loadHistoryEntry = useCallback(
    async (entryId: string) => {
      const entry = historyEntries.find(
        (candidate) => candidate.id === entryId,
      );
      if (!entry) {
        return;
      }
      dispatch(setReportGenerationSelectedHistoryEntry(entryId));
      try {
        if (entry.runId) {
          const state = await triggerGetAssistantSessionState(
            "getAssistantSessionState",
            {
              runId: entry.runId,
              module,
              pathname,
            },
          );
          const definition = parseHistoryDefinition(
            (
              state.activeRun?.response as
                | Record<string, unknown>
                | null
                | undefined
            )?.reportDefinition,
          );
          if (definition) {
            restoreDefinition(definition);
            setLastActionFeedback("Loaded report definition from run history.");
            return;
          }
        }

        if (entry.templateId) {
          const template = myTemplates.find(
            (candidate) => candidate.id === entry.templateId,
          );
          if (template?.reportDefinition) {
            restoreDefinition({
              ...template.reportDefinition,
              templateId: template.id,
              templateSource: "custom",
            });
            setLastActionFeedback("Loaded report template from history.");
            return;
          }
        }

        setLastActionFeedback(
          "This history entry does not expose a reusable report definition yet.",
        );
      } catch (error) {
        console.error("[ReportGeneration] Failed to load history entry", error);
        setLastActionFeedback("Unable to reload this history entry right now.");
      }
    },
    [
      historyEntries,
      module,
      myTemplates,
      pathname,
      restoreDefinition,
      triggerGetAssistantSessionState,
    ],
  );

  const startNewReport = useCallback(() => {
    filterModel.actions.startNewReport();
    dispatch(resetReportGenerationWorkspaceView());
    dispatch(
      openReportGenerationWorkspace({
        activeTab: "filters",
        reportTitle: routeContext.defaultTitle,
      }),
    );
    setLastActionFeedback(null);
  }, [dispatch, filterModel.actions, routeContext.defaultTitle]);

  const refreshWorkspaceData = useCallback(async () => {
    await workspaceQuery.refetch();
  }, [workspaceQuery]);

  const pollReportRunArtifacts = useCallback(
    async (pending: {
      runId: string | null;
      workItemId: string | null;
      requestedFormats: AssistantReportExportFormat[];
    }) => {
      if (!pending.runId && !pending.workItemId) {
        setPendingReportRun(null);
        return;
      }

      for (let attempt = 0; attempt < 12; attempt += 1) {
        await new Promise((resolve) =>
          setTimeout(resolve, attempt === 0 ? 750 : 2000),
        );
        const refreshed = (await workspaceQuery.refetch()) as {
          data?: NonNullable<typeof workspaceQuery.data>;
        };
        const items =
          refreshed.data?.history.items ?? workspaceQuery.data?.history.items ?? [];
        const matchingItem = items.find(
          (item) =>
            (pending.workItemId && item.id === pending.workItemId) ||
            (pending.runId && item.runId === pending.runId),
        );
        if (!matchingItem) {
          continue;
        }
        if (matchingItem.state === "failed") {
          setLastActionFeedback(
            matchingItem.statusMessage ??
              "Report run failed before producing artifacts.",
          );
          setPendingReportRun(null);
          return;
        }
        if (matchingItem.state === "cancelled") {
          setLastActionFeedback(
            "Report run was cancelled before producing artifacts.",
          );
          setPendingReportRun(null);
          return;
        }
        const artifactCount = matchingItem.artifactRefs?.length ?? 0;
        if (matchingItem.state === "completed" && artifactCount > 0) {
          setLastActionFeedback(
            `${resolvedTitle} is ready with ${artifactCount} downloadable artifact${artifactCount === 1 ? "" : "s"}.`,
          );
          setPendingReportRun(null);
          return;
        }
      }

      setLastActionFeedback(
        `${resolvedTitle} is still processing. History will show download links when artifacts are ready.`,
      );
      setPendingReportRun(null);
    },
    [workspaceQuery, resolvedTitle],
  );

  const launchReportRun = useCallback(
    async (exportFormats: AssistantReportExportFormat[]) => {
      filterModel.actions.runReport();
      if (!hasReportDefinitionSelection) {
        setLastActionFeedback(
          "Select at least one entity and one field before launching a report.",
        );
        return;
      }
      if (previewTransportError) {
        setLastActionFeedback(previewTransportError);
        return;
      }
      if (previewQuery.isLoading || previewQuery.isFetching) {
        setLastActionFeedback(
          "Report preview is still checking the current selection. Try again when the preview finishes loading.",
        );
        return;
      }
      if (!previewQuery.data) {
        setLastActionFeedback(
          "Report preview is not ready yet. Try again after the preview finishes checking the current selection.",
        );
        return;
      }
      if (previewQuery.data?.canRun === false) {
        setLastActionFeedback(
          previewWarnings[0] ??
            "Report preview is blocked for the current selection.",
        );
        return;
      }
      try {
        const payload = await triggerMutateAssistantRun({
          input: {
            action: "start",
            question: `Generate the report "${resolvedTitle}".`,
            conversationHistory: [],
            context: {
              pathname,
              capabilityModule: module,
              reportGeneration: {
                reportDefinition: runnableReportDefinition,
                exportFormats,
                reportTemplateId: currentCustomTemplateId,
                reportScheduleId: currentSchedule?.id ?? null,
              },
              assistantTask: {
                workflowKey: "assistant.report.generate",
                taskClass: "compute_report",
                title: resolvedTitle,
                summary: `Generate ${resolvedTitle}`,
              },
            },
          },
        });
        if (!payload.accepted) {
          setLastActionFeedback("Report run was not accepted.");
          return;
        }
        setLastActionFeedback(
          exportFormats.length === 1
            ? `${resolvedTitle} queued for ${exportFormats[0].toUpperCase()} export.`
            : `${resolvedTitle} queued. The run will publish exports into assistant history.`,
        );
        const pending = {
          runId: payload.run?.id ?? null,
          workItemId: payload.run?.workItemId ?? null,
          requestedFormats: [...exportFormats],
        };
        setPendingReportRun(pending);
        dispatch(setReportGenerationWorkspaceTab("history"));
        await refreshWorkspaceData();
        void pollReportRunArtifacts(pending);
      } catch (error) {
        console.error("[ReportGeneration] Failed to launch report run", error);
        setLastActionFeedback("Unable to launch the report right now.");
        setPendingReportRun(null);
      }
    },
    [
      currentCustomTemplateId,
      currentSchedule?.id,
      dispatch,
      filterModel.actions,
      hasReportDefinitionSelection,
      module,
      pathname,
      pollReportRunArtifacts,
      previewQuery.data,
      previewQuery.isFetching,
      previewQuery.isLoading,
      previewTransportError,
      previewWarnings,
      refreshWorkspaceData,
      resolvedTitle,
      runnableReportDefinition,
      triggerMutateAssistantRun,
    ],
  );

  const saveTemplate = useCallback(
    async (visibility: "private" | "shared") => {
      if (!hasReportDefinitionSelection) {
        setLastActionFeedback(
          "Select at least one entity and one field before saving a template.",
        );
        return;
      }
      try {
        const payload = await triggerMutateAssistantReportTemplate({
          input: {
            action: "upsert",
            templateId: currentCustomTemplateId ?? undefined,
            visibility,
            title: resolvedTitle,
            sourceTemplateId:
              filterModel.state.selectedTemplateSource === "gallery"
                ? (filterModel.state.selectedTemplateId ?? undefined)
                : undefined,
            reportDefinition: currentDefinition,
          },
        });
        const template = payload.template;
        if (template) {
          restoreDefinition({
            ...template.reportDefinition,
            title: template.title,
            templateId: template.id,
            templateSource: "custom",
          });
        }
        setLastActionFeedback(
          visibility === "shared"
            ? "Shared template saved."
            : "Private template saved.",
        );
        await refreshWorkspaceData();
      } catch (error) {
        console.error("[ReportGeneration] Failed to save template", error);
        setLastActionFeedback("Unable to save this template right now.");
      }
    },
    [
      currentCustomTemplateId,
      currentDefinition,
      filterModel.state.selectedTemplateId,
      filterModel.state.selectedTemplateSource,
      hasReportDefinitionSelection,
      refreshWorkspaceData,
      resolvedTitle,
      restoreDefinition,
      triggerMutateAssistantReportTemplate,
    ],
  );

  const toggleDailySchedule = useCallback(async () => {
    if (!hasReportDefinitionSelection) {
      setLastActionFeedback(
        "Select at least one entity and one field before scheduling a report.",
      );
      return;
    }
    try {
      if (currentSchedule) {
        await triggerMutateAssistantReportSchedule({
          input: {
            action: "toggle",
            scheduleId: currentSchedule.id,
            enabled: !currentSchedule.enabled,
          },
        });
        setLastActionFeedback(
          currentSchedule.enabled
            ? "Daily schedule paused."
            : "Daily schedule re-enabled.",
        );
      } else {
        await triggerMutateAssistantReportSchedule({
          input: {
            action: "upsert",
            title: resolvedTitle,
            reportTemplateId: currentCustomTemplateId ?? undefined,
            reportDefinition: currentDefinition,
            exportFormats: ["xlsx", "csv", "pdf"],
            scheduleExpression: "rate(1 day)",
            timezone: YSTNJ_REPORT_TIMEZONE,
            enabled: true,
          },
        });
        setLastActionFeedback("Daily schedule saved.");
      }
      await refreshWorkspaceData();
    } catch (error) {
      console.error(
        "[ReportGeneration] Failed to update report schedule",
        error,
      );
      setLastActionFeedback("Unable to update the schedule right now.");
    }
  }, [
    currentCustomTemplateId,
    currentDefinition,
    currentSchedule,
    hasReportDefinitionSelection,
    refreshWorkspaceData,
    resolvedTitle,
    triggerMutateAssistantReportSchedule,
  ]);

  const insights = useMemo<ReportGenerationInsightCard[]>(() => {
    const moduleLabel = resolveTemplateModuleLabel(currentDefinition);
    const selectedCount = currentDefinition.selectedFields.length;
    const previewWarnings = previewQuery.data?.warnings ?? [];
    const blockedEntityCount =
      previewQuery.data?.blockedEntityIds.length ??
      filterModel.viewModel.inaccessibleEntities.length;
    return [
      {
        id: "coverage",
        tone: previewRows.length > 0 ? "up" : "down",
        headline: `${selectedCount || 0} columns selected for ${moduleLabel}`,
        detail:
          previewRows.length > 0
            ? `${previewQuery.data?.totalRowCount ?? previewRows.length} records are available in the preview.`
            : "Choose at least one data category and field to preview this report.",
      },
      {
        id: "joins",
        tone:
          (previewQuery.data?.joins.length ??
            filterModel.viewModel.joins.length) > 0
            ? "alert"
            : "down",
        headline:
          (previewQuery.data?.joins.length ??
            filterModel.viewModel.joins.length) > 0
            ? `${previewQuery.data?.joins.length ?? filterModel.viewModel.joins.length} related data sources included`
            : "One data source selected",
        detail:
          (previewQuery.data?.joins ?? filterModel.viewModel.joins).length > 0
            ? "This report combines related school records for a single view."
            : "Add another compatible data category if you need a combined report.",
      },
      {
        id: "permissions",
        tone:
          blockedEntityCount > 0 || previewWarnings.length > 0 ? "alert" : "up",
        headline:
          blockedEntityCount > 0
            ? `${blockedEntityCount} data categories are unavailable`
            : "All selected data is available",
        detail:
          blockedEntityCount > 0 || previewWarnings.length > 0
            ? "Choose another data category, or contact support if you need access to this information."
            : "You can preview and run this report.",
      },
    ];
  }, [
    currentDefinition,
    filterModel.viewModel.inaccessibleEntities.length,
    filterModel.viewModel.joins,
    previewQuery.data?.blockedEntityIds.length,
    previewQuery.data?.joins,
    previewQuery.data?.totalRowCount,
    previewQuery.data?.warnings,
    previewRows.length,
  ]);

  const visibleInsights = useMemo(
    () =>
      insights.filter(
        (card) => !workspace.dismissedInsightIds.includes(card.id),
      ),
    [insights, workspace.dismissedInsightIds],
  );

  const versionHistoryByTemplateId = useMemo<
    Record<string, ReportGenerationVersionHistoryEntry[]>
  >(
    () =>
      Object.fromEntries(
        myTemplates.map((template) => [
          template.id,
          [
            {
              version: template.version ?? "1",
              date: template.lastModified ?? "",
              summary: template.description,
            },
          ],
        ]),
      ),
    [myTemplates],
  );

  const actions = useMemo(
    () => ({
      openWorkspace: (tab: ReportGenerationWorkspaceTab = "templates") => {
        dispatch(
          openReportGenerationWorkspace({
            activeTab: tab,
            reportTitle: routeContext.defaultTitle || resolvedTitle,
          }),
        );
      },
      closeWorkspace: () => dispatch(closeReportGenerationWorkspace()),
      setCollapsed: (collapsed: boolean) =>
        dispatch(setReportGenerationWorkspaceCollapsed(collapsed)),
      setActiveTab: (tab: ReportGenerationWorkspaceTab) =>
        dispatch(setReportGenerationWorkspaceTab(tab)),
      setTemplateView: (view: "gallery" | "my") =>
        dispatch(setReportGenerationTemplateView(view)),
      openVersionHistory: (templateId: string) =>
        dispatch(openReportGenerationTemplateVersionHistory(templateId)),
      closeVersionHistory: () =>
        dispatch(closeReportGenerationTemplateVersionHistory()),
      setSelectedHistoryEntry: (entryId: string | null) =>
        dispatch(setReportGenerationSelectedHistoryEntry(entryId)),
      setReportTitle: (title: string) =>
        dispatch(setReportGenerationWorkspaceTitle(title)),
      toggleSort: (fieldId: string) =>
        dispatch(toggleReportGenerationWorkspaceSort(fieldId)),
      toggleHiddenColumn: (fieldId: string) =>
        dispatch(toggleReportGenerationHiddenColumn(fieldId)),
      setContextMenuField: (fieldId: string | null) =>
        dispatch(setReportGenerationContextMenuField(fieldId)),
      openExport: () => dispatch(openReportGenerationExport()),
      closeExport: () => dispatch(closeReportGenerationExport()),
      dismissInsight: (insightId: string) =>
        dispatch(dismissReportGenerationInsight(insightId)),
      applyTemplateById,
      loadHistoryEntry,
      startNewReport,
      runReport: () => launchReportRun(["xlsx", "csv", "pdf"]),
      runExport: (format: AssistantReportExportFormat) => {
        dispatch(closeReportGenerationExport());
        void launchReportRun([format]);
      },
      runExportBundle: () => {
        dispatch(closeReportGenerationExport());
        void launchReportRun(["xlsx", "csv", "pdf"]);
      },
      savePrivateTemplate: () => void saveTemplate("private"),
      saveSharedTemplate: () => void saveTemplate("shared"),
      toggleDailySchedule: () => void toggleDailySchedule(),
      setEntitySearch: filterModel.actions.setEntitySearch,
      toggleEntity: filterModel.actions.toggleEntity,
      toggleField: filterModel.actions.toggleField,
      addCondition: filterModel.actions.addCondition,
      updateCondition: filterModel.actions.updateCondition,
      removeCondition: filterModel.actions.removeCondition,
      updateDateRange: filterModel.actions.updateDateRange,
      setPrimarySortField: filterModel.actions.setPrimarySortField,
      togglePrimarySortDirection:
        filterModel.actions.togglePrimarySortDirection,
      clearAll: filterModel.actions.clearAll,
    }),
    [
      applyTemplateById,
      dispatch,
      filterModel.actions,
      launchReportRun,
      loadHistoryEntry,
      resolvedTitle,
      routeContext.defaultTitle,
      saveTemplate,
      startNewReport,
      toggleDailySchedule,
    ],
  );

  return {
    profile: filterModel.profile,
    effectivePersona: filterModel.effectivePersona,
    filterState: filterModel.state,
    routeContext,
    definition: currentDefinition,
    workspace,
    resolvedTitle,
    galleryTemplates,
    myTemplates,
    historyEntries,
    versionHistoryByTemplateId,
    insights: visibleInsights,
    modules: filterModel.viewModel.modules,
    inaccessibleEntities: filterModel.viewModel.inaccessibleEntities,
    joins,
    availableFields: filterModel.viewModel.availableFields,
    previewColumns,
    previewRows,
    advancedVisualization,
    hasReportDefinitionSelection,
    canPreview,
    previewLoading: previewQuery.isLoading || previewQuery.isFetching,
    previewError: previewTransportError,
    previewWarnings,
    totalRowCount,
    isSavingTemplate: mutateTemplateState.isLoading,
    isScheduling: mutateScheduleState.isLoading,
    isRunningReport: mutateRunState.isLoading || pendingReportRun !== null,
    lastActionFeedback,
    hasActiveSchedule: Boolean(currentSchedule?.enabled),
    actions,
  };
};
